/* eslint-disable class-methods-use-this -- Stateless API transport wrappers. */
import RecruitApiClient from '../../RecruitApiClient';

import type {
  IApprovalFormRequestParams,
  IApprovalPublicLink,
  IApprovalPublicLinkRequestParams,
  IApprovalTemplate,
  IApprovalTemplateCreators
} from './ApprovalTemplates/types';
import type {
  IApprovalForm,
  IApprovalManagers,
  IApprovalTemplateFields,
  IAssignableUsers,
  IFilterOptionsResponse,
  IRequestParams,
  IRequisitionForm,
  IRequisitionPublicLink
} from './types';
import type { IJob } from '../Jobs/types';

const BASE_URL = '/api/v4';

class Api {
  getEntityApprovers = async () =>
    RecruitApiClient.get<{ entity_default_approvers: string[] }>(
      `${BASE_URL}/requisition_managers/entity_default_approvers`
    );

  getApprovalManagers = async (params: object) =>
    RecruitApiClient.get<{ requisition_managers: IApprovalManagers[] }>(
      `${BASE_URL}/requisition_managers`,
      params
    );

  createApprovalManager = async (params: {
    requisition_manager: { name: string; email: string };
  }) => RecruitApiClient.post<{ success: boolean }>(`${BASE_URL}/requisition_managers`, params);

  deleteApprovalManager = async (approvalManagerId: number) =>
    RecruitApiClient.delete<{ success: boolean }>(
      `${BASE_URL}/requisition_managers/${approvalManagerId}`
    );

  deleteApprovalManagers = async (params: { requisition_manager_ids: number[] }) =>
    RecruitApiClient.delete<{ requisition_manager_ids: { failure: string[] } }>(
      `${BASE_URL}/requisition_managers/bulk_destroy`,
      params
    );

  updateApprovalManager = async (
    params: { requisition_manager: { name: string; email: string } },
    approvalManagerId: number
  ) =>
    RecruitApiClient.put<{ success: string }>(
      `${BASE_URL}/requisition_managers/${approvalManagerId}`,
      params
    );

  getEmailTemplates = async () =>
    RecruitApiClient.get<{ email_templates: [string, { id: number; name: string }[]][] }>(
      `${BASE_URL}/requisition_forms/email_templates`
    );

  getEnabledApprovalTemplates = async () =>
    RecruitApiClient.get<{ enabled_requisition_forms: [] }>(
      `${BASE_URL}/requisition_forms/enabled_for_applications`
    );

  createApprovalTemplate = async (params: { requisition_form: IRequisitionForm }) =>
    RecruitApiClient.post<{ id: string; link: string }>(`${BASE_URL}/requisition_forms`, params);

  updateApprovalTemplate = async (
    approvalTemplateId: number,
    params: { requisition_form: Partial<IRequisitionForm> }
  ) => RecruitApiClient.put(`${BASE_URL}/requisition_forms/${approvalTemplateId}`, params);

  cloneApprovalTemplate = async (
    approvalTemplateId: number,
    params: { requisition_form: IRequisitionForm }
  ) =>
    RecruitApiClient.post<{ success: string; redirect_url: string }>(
      `${BASE_URL}/requisition_forms/${approvalTemplateId}/clone`,
      params
    );

  getApprovalTemplate = async (approvalTemplateId: number) =>
    RecruitApiClient.get<IApprovalTemplate>(`${BASE_URL}/requisition_forms/${approvalTemplateId}`);

  getApprovalTemplates = async (params: IApprovalFormRequestParams) =>
    RecruitApiClient.get<{ requisition_forms: IApprovalTemplate[] }>(
      `${BASE_URL}/requisition_forms`,
      params
    );

  getApprovalForms = async (params: IRequestParams) =>
    RecruitApiClient.get<{ requisitions: IApprovalForm[] }>(`${BASE_URL}/requisitions`, params);

  getApprovalForm = async (approvalFormId: number) =>
    RecruitApiClient.get<IApprovalForm>(`${BASE_URL}/requisitions/${approvalFormId}`);

  putApprovalForm = async (approvalFormId: number, params: Record<string, unknown>) =>
    RecruitApiClient.put<{ success: string }>(`${BASE_URL}/requisitions/${approvalFormId}`, params);

  putApproversState = async (requisitionApproverId: number, params: Record<string, unknown>) =>
    RecruitApiClient.put<{ success: string }>(
      `${BASE_URL}/requisition_approvers/${requisitionApproverId}`,
      params
    );

  postResendEmail = async (approvalFormId: number, params: { approver_id: number }) =>
    RecruitApiClient.post(`${BASE_URL}/requisitions/${approvalFormId}/resend_email`, params);

  cloneApprovalForm = async (approvalFormId: number) =>
    RecruitApiClient.post<{ success: string; url: string }>(
      `${BASE_URL}/requisitions/${approvalFormId}/clone`
    );

  generateBulkPdf = async (approvalFormId: number) =>
    RecruitApiClient.post<{ success: string }>(
      `${BASE_URL}/requisitions/${approvalFormId}/bulk_pdf`
    );

  getTargetEntities = async () =>
    RecruitApiClient.get<{ entities: { id: number; name: string }[] }>(
      `${BASE_URL}/target_entities`
    );

  moveApprovalTemplate = async (approvalTemplateId: number, entityId: string) =>
    RecruitApiClient.post(
      `${BASE_URL}/requisition_forms/${approvalTemplateId}/move?requisition_form[entity_id]=${entityId}`
    );

  moveApprovalForm = async (approvalFormId: number, entityId: number) =>
    RecruitApiClient.post(
      `${BASE_URL}/requisitions/${approvalFormId}/move?requisition[entity_id]=${entityId}`
    );

  checkApproversAvailability = async (approvalFormId: number, entityId: number) =>
    RecruitApiClient.get<{
      all_approvers_available: boolean;
      unavailable_approvers: { id: number; email: string; name: string; state: string }[];
    }>(`${BASE_URL}/requisitions/${approvalFormId}/move_availability`, {
      target_entity_id: entityId
    });

  getCreators = async (params: { entity_id?: number } = {}) =>
    RecruitApiClient.get<{ creators: IApprovalTemplateCreators[] }>(
      `${BASE_URL}/requisition_forms/creators`,
      params
    );

  getApprovalPublicLinks = async (params: IApprovalPublicLinkRequestParams) =>
    RecruitApiClient.get<{ requisition_public_links: IApprovalPublicLink[] }>(
      `${BASE_URL}/requisition_public_links`,
      params
    );

  createApprovalPublicLink = async (params: { requisition_public_link: IRequisitionPublicLink }) =>
    RecruitApiClient.post<{ success: string }>(`${BASE_URL}/requisition_public_links`, params);

  updateApprovalPublicLink = async (
    publicLinkId: number,
    params: { requisition_public_link: IRequisitionPublicLink }
  ) =>
    RecruitApiClient.put<{ success: string }>(
      `${BASE_URL}/requisition_public_links/${publicLinkId}`,
      params
    );

  deleteApprovalPublicLinks = async (params: object) =>
    RecruitApiClient.delete<{ requisition_public_link_ids: { failure: []; success: [] } }>(
      `${BASE_URL}/requisition_public_links/bulk_destroy`,
      params
    );

  newField = async (approvalTemplateId: number, params: { field: { body: string } }) =>
    RecruitApiClient.post(`${BASE_URL}/fields?requisition_form_id=${approvalTemplateId}`, params);

  addSystemField = async (approvalTemplateId: number, systemFieldId: number) =>
    RecruitApiClient.post(
      `${BASE_URL}/system_fields?requisition_form_id=${approvalTemplateId}&system_field_id=${systemFieldId}`
    );

  getQuestions = async (params: { entity_id: number; requisition_form_id: number }) =>
    RecruitApiClient.get<{ fields: IApprovalTemplateFields[] }>(`${BASE_URL}/fields`, params);

  deleteQuestion = async (id: number, approvalTemplateId: number) =>
    RecruitApiClient.delete<{ errors: string }>(
      `${BASE_URL}/fields/${id}?requisition_form_id=${approvalTemplateId}`
    );

  cloneQuestion = async (id: number, approvalTemplateId: number) =>
    RecruitApiClient.post(
      `${BASE_URL}/fields/${id}/clone?requisition_form_id=${approvalTemplateId}`
    );

  putQuestion = async (
    id: number,
    approvalTemplateId: number,
    params: { field: { body: string } }
  ) =>
    RecruitApiClient.put(
      `${BASE_URL}/fields/${id}?requisition_form_id=${approvalTemplateId}`,
      params
    );

  postArchiveApprovalForms = async (params: object) =>
    RecruitApiClient.post<{ requisition_ids: { failure: number[]; success: number[] } }>(
      `${BASE_URL}/requisitions/bulk_archive`,
      params
    );

  postUnarchiveApprovalForms = async (params: object) =>
    RecruitApiClient.post<{ requisition_ids: { failure: number[]; success: number[] } }>(
      `${BASE_URL}/requisitions/bulk_unarchive`,
      params
    );

  deleteTrashApprovalForms = async (params: object) =>
    RecruitApiClient.delete<{ requisition_ids: { failure: []; success: [] } }>(
      `${BASE_URL}/requisitions/bulk_destroy`,
      params
    );

  postArchiveApprovalTemplates = async (params: object) =>
    RecruitApiClient.post<{ approval_template_ids: { failure: number[]; success: number[] } }>(
      `${BASE_URL}/requisition_forms/bulk_archive`,
      params
    );

  postUnarchiveApprovalTemplates = async (params: object) =>
    RecruitApiClient.post<{ approval_template_ids: { failure: number[]; success: number[] } }>(
      `${BASE_URL}/requisition_forms/bulk_unarchive`,
      params
    );

  deleteTrashApprovalTemplates = async (params: object) =>
    RecruitApiClient.delete<{ success: string }>(
      `${BASE_URL}/requisition_forms/bulk_destroy`,
      params
    );

  getAssignableUsers = async () =>
    RecruitApiClient.get<{ assignable_users: IAssignableUsers[] }>(`${BASE_URL}/assignable_users`);

  getCSVData = async (questionId: number, positionId: number) =>
    RecruitApiClient.get(`${BASE_URL}/system_fields/${questionId}/remote`, {
      position_id: positionId,
      new_position: false
    });

  getJobs = async () =>
    RecruitApiClient.get<{ jobs: IJob[] }>(`${BASE_URL}/jobs/jobs_for_requisitions`);

  getApplications = async (jobId: number) =>
    RecruitApiClient.get<{ id: number; candidate_name: string }[]>(
      `${BASE_URL}/jobs/${jobId}/applications_for_requisition`
    );

  getFilters = async <TKey extends keyof IFilterOptionsResponse>(
    url: TKey,
    params: { archived: boolean }
  ) =>
    RecruitApiClient.get<Pick<IFilterOptionsResponse, TKey>>(
      `${BASE_URL}/requisitions/${url}`,
      params
    );
}

// eslint-disable-next-line import-x/no-anonymous-default-export -- Existing callers use this singleton as Api.
export default new Api();
